<!-- ── Legend : lithology ratio buckets | drill log discontinuities ────────── -->
<aside class="holes-view__legend" data-attr="holes-legend-geology">
  <app-holes-legend-panel>
    <div class="geo-legend">

      <!-- Mode 1 : lithology share of the hole -->
      <app-radiobox
        size="small"
        [options]="lithologyModeOption()"
        [ngModel]="mode()"
        (ngModelChange)="onModeChange($event)"
        data-attr="holes-geology-mode-lithology">
      </app-radiobox>

      <div class="geo-legend__section" [class.geo-legend__section--inactive]="mode() !== 'lithology'">
        @if (is3d() && lithologyLayers().items.length) {
          <ng-container *ngTemplateOutlet="modelLayers; context: { $implicit: lithologyLayers() }" />
        } @else if (availableLithologies().length) {
          <app-select
            size="small"
            [options]="lithologyOptions()"
            [ngModel]="lithologyId()"
            (ngModelChange)="onLithologyChange($event)"
            [placeholder]="'HOLES_TAB.GEOLOGY_SELECT_LITHOLOGY' | translate"
            data-attr="holes-geology-lithology">
          </app-select>

          <!-- Four shades of the lithology's own colour, ]0 %;100 %] of the hole; 0 % is off the scale -->
          <ul class="geo-legend__buckets">
            @for (step of gradientSteps(); track step.from) {
              <li class="geo-legend__item">
                <span class="geo-legend__swatch" [style.background]="step.color"></span>
                <span>{{ 'HOLES_TAB.GEOLOGY_GRADIENT_STEP' | translate: { from: step.from, to: step.to, name: selectedLithologyName() } }}</span>
              </li>
            }
          </ul>
        } @else if (referencesError()) {
          <p class="geo-legend__empty geo-legend__empty--error">{{ 'HOLES_TAB.GEOLOGY_REFERENCES_ERROR' | translate }}</p>
        } @else if (state.geologyLoading() || !state.geology()) {
          <!-- geology still loading: the panel already shows the notice -->
        } @else if (lithologies().length) {
          <p class="geo-legend__empty">{{ 'HOLES_TAB.GEOLOGY_NO_LITHOLOGIES_IN_BLAST' | translate }}</p>
        } @else {
          <p class="geo-legend__empty">{{ 'HOLES_TAB.GEOLOGY_NO_LITHOLOGIES' | translate }}</p>
        }
      </div>

      <!-- Mode 2 : drill log discontinuities -->
      <app-radiobox
        size="small"
        [options]="drillLogModeOption()"
        [ngModel]="mode()"
        (ngModelChange)="onModeChange($event)"
        data-attr="holes-geology-mode-drilllog">
      </app-radiobox>

      <div class="geo-legend__section" [class.geo-legend__section--inactive]="mode() !== 'drillLog'">
        @if (is3d() && discontinuityLayers().items.length) {
          <ng-container *ngTemplateOutlet="modelLayers; context: { $implicit: discontinuityLayers() }" />
        } @else if (availableDiscontinuities().length) {
          <app-radiobox
            size="small"
            iconPosition="left"
            [fullWidth]="true"
            [options]="discontinuityOptions()"
            [ngModel]="discontinuityIdValue()"
            (ngModelChange)="onDiscontinuityChange($event)"
            data-attr="holes-geology-discontinuity">
            @for (discontinuity of availableDiscontinuities(); track discontinuity.id) {
              <ng-template [appRadioIcon]="'' + discontinuity.id">
                <span class="geo-legend__swatch" [style.background]="discontinuity.color"></span>
              </ng-template>
            }
          </app-radiobox>
        } @else if (referencesError()) {
          <!-- the lithology section already shows the reference-list error -->
        } @else if (state.geologyLoading() || !state.geology()) {
          <!-- geology still loading: the panel already shows the notice -->
        } @else if (discontinuities().length) {
          <p class="geo-legend__empty">{{ 'HOLES_TAB.GEOLOGY_NO_DISCONTINUITIES_IN_BLAST' | translate }}</p>
        } @else {
          <p class="geo-legend__empty">{{ 'HOLES_TAB.GEOLOGY_NO_DISCONTINUITIES' | translate }}</p>
        }
      </div>

    </div>
  </app-holes-legend-panel>
</aside>

<!-- 3D : couches du modèle en cubes, chacune affichable ou masquable, avec sa part de la longueur relevée -->
<ng-template #modelLayers let-layers>
  <div class="geo-layers" [attr.data-attr]="'holes-geology-layers-' + layers.kind">
    <div class="geo-layers__head">
      <span>{{ 'HOLES_TAB.GEOLOGY_3D_LAYERS' | translate }}</span>
      <button type="button" class="geo-layers__all" (click)="onToggleAllLayers(layers)">
        {{ (layers.anyHidden ? 'HOLES_TAB.GEOLOGY_3D_SHOW_ALL' : 'HOLES_TAB.GEOLOGY_3D_HIDE_ALL') | translate }}
      </button>
    </div>

    <ul class="geo-layers__list">
      @for (layer of layers.items; track layer.id) {
        <li class="geo-layers__item" [class.geo-layers__item--hidden]="layer.hidden">
          <app-checkbox
            size="small"
            iconPosition="left"
            [fullWidth]="true"
            [label]="layer.name"
            [ngModel]="!layer.hidden"
            (changed)="onLayerToggle(layers.kind, layer.id, $event)"
            [attr.data-attr]="'holes-geology-layer-' + layer.id">
            <ng-template appCheckboxIcon>
              <span class="geo-legend__swatch" [style.background]="layer.color"></span>
            </ng-template>
          </app-checkbox>
          <span class="geo-layers__share" [title]="'HOLES_TAB.GEOLOGY_3D_SHARE' | translate">{{ layer.share }} %</span>
        </li>
      }
    </ul>
  </div>
</ng-template>

<!-- ── Panel : per-hole geology table ───────────────────────────────────────── -->
<section class="holes-view__panel" data-attr="holes-panel-geology">

  @if (state.geologyError()) {
    <div class="geo-panel__notice geo-panel__notice--error">
      <span>
        {{ 'HOLES_TAB.GEOLOGY_LOAD_ERROR' | translate }}
        @if (state.geologyErrorMessage(); as detail) {
          <small class="geo-panel__detail" data-attr="holes-geology-error-detail">{{ detail }}</small>
        }
      </span>
      <button type="button" class="geo-panel__retry" (click)="retryGeology()">{{ 'HOLES_TAB.RETRY' | translate }}</button>
    </div>
  } @else if (state.geologyLoading()) {
    <div class="geo-panel__notice">…</div>
  }

  <div class="holes-view__head">
    <div class="holes-view__columns">
      <app-button-list
        [items]="allColumns"
        [selectedKeys]="visibleColumnKeys()"
        (selectionChange)="onColumnsChange($event)"
        iconSrc="assets/icon/settings.svg"
        panelTitle="QAQC_TAB.COLUMNS" />
    </div>
  </div>

  <div class="holes-view__table">

    @if (rows().length) {
      <app-data-table
        [data]="rows()"
        [columns]="visibleColumns()"
        [searchTerm]="state.searchTerm()"
        [clickable]="true"
        rowKey="publicGuid"
        highlightedRowKey="publicGuid"
        [highlightedRowValue]="state.selectedHoleId()"
        (selectionLigne)="onRowClick($event)"
        sortAscIcon="assets/icon/new_icon/arrow_full_down.svg"
        sortDescIcon="assets/icon/new_icon/arrow_full_up.svg"
        sortIconFilter="brightness(0)" />
    } @else if (!state.isLoading()) {
      <p class="holes-view__empty">{{ 'HOLES_TAB.NO_HOLES' | translate }}</p>
    }
  </div>

</section>
